import { Component, computed, EnvironmentInjector, HostListener, inject, OnDestroy, OnInit, runInInjectionContext, signal, Signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { CacheService } from '@app/_services/cache.service';
import { SvgComponent } from '../shared/svg/svg.component';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { AlertService } from '@app/_services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackDialogComponent } from '../shared/dialogs/feedback-dialog/feedback-dialog.component';
import { HabitTimerService } from '@app/_services/habit-timer.service';
import { SettingsService } from '@app/_services/settings.service';
import { NotificationService } from '@app/_services/notification.service';
import { StripeService } from '@app/_services/stripe.service';
import { MeUserAvatarComponent } from '../common/account/me-user-avatar/me-user-avatar.component';
import { UserService } from '@app/_services/user.service';
import { CloudSyncService } from '@app/_services/cloud-sync.service';
import { MatMenuModule } from '@angular/material/menu';
import { AnalyticsService } from '@app/_services/analytics.service';
import { AuthService } from '@app/_services/auth.service';
import { SessionGuardService } from '@app/_services/session-guard.service';
import { InsightComponent } from '../addons/insights/insight/insight.component';
import { log } from '@app/_services/logger';
import { DebugScreenComponent } from '../debug-screen/debug-screen.component';
import { FeatureStore, PublicUserStore, ViewSettingsStore } from '@app/_stores';
import { isMobileViewport } from '@app/_configs/layout.config';
import { BRAND_LOGO } from '@app/_utils/brand-icons';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';
import { ModalService } from '@app/_services/modal.service';
import { HabitTimerTrackerComponent } from '../shared/habit-timer-tracker/habit-timer-tracker.component';
import { PastFuturePrefetchService } from '@app/_services/past-future-prefetch.service';
import { IndexDbService } from '@app/_services/index-db.service';
import { MeoStore } from '@app/_stores/meo.store';
import { MeoPageComponent } from '../meo/meo-page/meo-page.component';

@Component({
  selector: 'app-panel',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    SvgComponent,
    MeUserAvatarComponent,
    MatMenuModule,
    HabitTimerTrackerComponent,
    MeoPageComponent
  ],
  templateUrl: './panel.component.html',
  styleUrl: './panel.component.scss'
})

export class PanelComponent implements OnInit, OnDestroy {

  readonly featureStore = inject(FeatureStore);
  readonly viewSettings = inject(ViewSettingsStore);
  readonly publicUserStore = inject(PublicUserStore);
  readonly meoStore = inject(MeoStore);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly prefetchService = inject(PastFuturePrefetchService);
  private readonly idbService = inject(IndexDbService);
  readonly brandLogo = BRAND_LOGO;
  unSubscribe = new Subject<void>();
  mevolveId: Signal<string> = computed(() => {
    return this.publicUserStore.mevolveId();
  });
  profileImageSrc: Signal<string | null> = computed(() => {
    const profileImageData = this.publicUserStore.profileImageData();
    if (!profileImageData) return null;
    return `data:image/jpeg;base64,${this.publicUserStore.profileImageData()}`;
  });

  currentScreen: Signal<string> = computed(() => {
    return this.as.currentScreen();
  });

  // True when the app download banner is currently shown
  bannerVisible = signal(this.isBannerVisible());

  private isBannerVisible(): boolean {
    const storedDate = localStorage.getItem('appBannerDate');
    const today = new Date().toISOString().slice(0, 10);
    if (storedDate === today) return false;
    return !!this.detectBannerPlatform();
  }

  private detectBannerPlatform(): string | null {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/windows nt/i.test(ua)) return 'windows';
    if (/macintosh|mac os x/i.test(ua) && !/chrome/i.test(ua)) return 'macos';
    return null;
  }

  // Responsive layout signals - uses breakpoint from layout.config.ts
  private screenWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1200);

  /**
   * Determines the mat-drawer mode based on screen width.
   * - 'side': Desktop mode - sidebar pushes content
   * - 'over': Mobile mode - sidebar overlays with backdrop
   */
  drawerMode = computed(() => isMobileViewport(this.screenWidth()) ? 'over' : 'side');

  /**
   * True when viewport is below the mobile breakpoint.
   * Used for applying mobile-specific CSS classes.
   */
  isMobileLayout = computed(() => isMobileViewport(this.screenWidth()));

  @HostListener('window:resize')
  onWindowResize() {
    this.screenWidth.set(window.innerWidth);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.meoStore.isPanelOpen()) {
      if (this.meoStore.showHistory()) {
        this.meoStore.toggleHistory();
      } else if (this.meoStore.showDebug()) {
        this.meoStore.toggleDebug();
      } else {
        this.meoStore.closePanel();
      }
    }
  }

  pressTimer: any;
  currentSegment: string = '';
  currentSegmentId: string = '';
  publicSegments: string[] = ['user', 'note', 'list'];
  // Routes that should not be redirected (valid authenticated routes)
  protectedRoutes: string[] = [
    'today',
    'intro-screen',
    'past',
    'future',
    'lists',
    'features',
    'insight',
    'attachment',
    'trash',
    'collaborators',
    'messages',
    'account-public',
    'settings',
    'feature-guide',
    'feedback',
    'focus-tracker',
    'meo',
    'meo-history',
    'chat',
    'shared',
  ];

  constructor(
    public ss: SettingsService,
    public notificationService: NotificationService,
    public cc: CacheService,
    public vs: ViewSettingService,
    private alertService: AlertService,
    public dialog: MatDialog,
    private habitTimerService: HabitTimerService,
    private stripeService: StripeService,
    public router: Router,
    private route: ActivatedRoute,
    public userService: UserService,
    private syncService: CloudSyncService,
    public as: AnalyticsService,
    public authService: AuthService,
    private sessionGuard: SessionGuardService,
    private snackBar: MatSnackBar,
    private modalService: ModalService
  ) {
    const path = this.router.url
    const segments = path.split('/').filter(Boolean);
    this.currentSegment = segments[0];
    this.currentSegmentId = segments[1];

  }

  isPublicProfileActive(): boolean {
    return this.router.isActive('/account-public', true);
  }

  async ngOnInit() {
    // ═══════════════════════════════════════════════════════════════════════
    // MULTI-TAB ISOLATION: Subscribe to force logout events FIRST
    // This must be set up before authService.init() so we can handle
    // cross-tab logout events that may occur during initialization
    // ═══════════════════════════════════════════════════════════════════════
    this.sessionGuard.forceLogout$.pipe(
      takeUntil(this.unSubscribe)
    ).subscribe((event) => {
      log.info('PanelComponent', 'Force logout received', event);

      // Show persistent toast notification to user (stays until dismissed)
      this.showForceLogoutNotification(event.reason);

      // Perform the actual logout
      this.authService.logoutUser();
    });

    this.ss.init();
    this.userService.init();
    this.clickNav();

    // cc.init() validates user has required properties (uid, userInfo, email)
    // Returns false if user is invalid or missing required properties
    const isValidUser = await this.cc.init();

    if (!isValidUser) {
      // User is invalid or not logged in - handle public links or redirect to login
      this.checkValidPublicLink(false);
      return;
    }

    // Additional safety check - ensure user data is properly set
    if (!this.cc.user?.uid) {
      this.checkValidPublicLink(false);
      return;
    }

    // Use runInInjectionContext for Firebase-related services to maintain injection context
    // after async operations in zoneless mode
    runInInjectionContext(this.envInjector, () => {
      this.habitTimerService.init();
      this.notificationService.init();
      this.stripeService.init();
      this.syncService.init();
    });
    // Await authService.init() to ensure user session is ready before user interactions
    // Wrap in runInInjectionContext because authService.init() triggers Firebase operations
    // (idb.resync, idb.onInit) that internally use inject()
    await runInInjectionContext(this.envInjector, () => this.authService.init());

    // Wait for viewSettings to load before routing
    await this.waitForViewSettings();
    this.checkValidPublicLink(true);

    // Prefetch first page of Past/Future data in the background so the user
    // sees no loader on first navigation. We fire both immediately in a
    // setTimeout(0) once the cache is ready — the internal setTimeout(0) in
    // the prefetch service guarantees these run off the current task without
    // blocking the main thread. No extra outer delay is needed because
    // cache$ only emits once data is fully loaded.
    this.cc.cache$.pipe(filter(r => !!r), take(1)).subscribe(() => {
      this.prefetchService.prefetch('PAST');
      this.prefetchService.prefetch('FUTURE');
    });

    // Re-trigger prefetch once IDB finishes loading all collections into the Angular stores.
    // cache$ fires before loadIndexedDBToUI completes, so prefetch may run with an empty entity
    // store (entity count: 0 → cache skipped). This subscription retries after entities are ready.
    this.idbService.idbLoaded$.pipe(take(1)).subscribe(() => {
      this.prefetchService.prefetch('PAST');
      this.prefetchService.prefetch('FUTURE');
    });

    // Close Meo panel on route navigation (e.g., sidebar link clicks)
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.unSubscribe)
    ).subscribe(() => {
      if (this.meoStore.isPanelOpen()) {
        this.meoStore.closePanel();
      }
    });
  };

  /**
   * Wait for viewSettings to be loaded from IndexedDB
   * Timeout after 2 seconds to prevent infinite waiting
   */
  private async waitForViewSettings(): Promise<void> {
    return new Promise((resolve) => {
      const maxWaitTime = 800; // 800 milliseconds
      const checkInterval = 50; // Check every 50ms
      let elapsed = 0;

      const intervalId = setInterval(() => {
        const viewSettings = this.ss.viewSettings();
        const isLoaded = Object.keys(viewSettings).length > 0;

        if (isLoaded || elapsed >= maxWaitTime) {
          clearInterval(intervalId);
          resolve();
        }

        elapsed += checkInterval;
      }, checkInterval);
    });
  }

  checkValidPublicLink(isLoggedIn: boolean) {
    // Get current route segments from router (not constructor-time values)
    // This ensures we check the actual current route after any redirects
    const path = this.router.url;
    const segments = path.split('/').filter(Boolean);
    const currentSegment = segments[0];
    const currentSegmentId = segments[1];

    // Protected routes require authentication — redirect to login if not logged in
    if (this.protectedRoutes.includes(currentSegment)) {
      if (!isLoggedIn) {
        this.router.navigate(['/login']);
      }
      return;
    }

    // Handle public segments (user, note, list)
    if (this.publicSegments.includes(currentSegment)) {
      if (currentSegmentId) {
        return; // Valid public link with ID
      } else {
        // Public segment without ID, redirect
        this.router.navigate([isLoggedIn ? this.getDefaultHomeRoute() : '/login']);
      }
    } else {
      // Unknown route, redirect to home/login
      this.router.navigate([isLoggedIn ? this.getDefaultHomeRoute() : '/login']);
    }
  }

  clickNav() {
    // Close sidebar on navigation for mobile viewports
    // Breakpoint configured in: _configs/layout.config.ts
    if (this.isMobileLayout()) {
      this.ss.showSidebar.set(false);
    }
  }

  async openPublicProfile() {
    this.clickNav();
    if (this.mevolveId()) {
      this.router.navigate(['/account-public']);
      return;
    } else {
      const res = await this.alertService.confirmRevamped({
        title: this.ss.t('completePublicProfile_title', { brandName: BRAND_CONFIG.name }),
        description: this.ss.t('completePublicProfile_content', { brandName: BRAND_CONFIG.name }),
        yesButtonText: this.ss.t('newHomeWidget_add'),
        noButtonText: this.ss.t('common_buttonCancel'),
        yesButtonColor: 'color-35',
        noButtonColor: 'color-7',
      });
      if (res) {
        this.userService.editMevolveId();
      } else {
        return;
      }
    }
  }

  openInsight() {
    this.clickNav();
    this.modalService.openLevel3(InsightComponent, {}, {
      disableClose: true
    });
  }

  openFeedbackDialog() {
    this.clickNav();

    if (this.cc.isNarrowScreen()) {
      this.modalService.openMainContent(
        FeedbackDialogComponent,
        {},
        { disableClose: true }
      );
    } else {
      this.modalService.openModalLevel1(
        FeedbackDialogComponent,
        {},
        { disableClose: true }
      );
    }
  };

  startPressToOpenDebug() {
    this.pressTimer = setTimeout(() => {
      this.openDebugScreen();
    }, 1200); // long press duration (ms)
  }

  endPressToOpenDebug() {
    clearTimeout(this.pressTimer);
  }

  /**
   * Determines the default home route based on enabled features.
   * Priority: Today (if date-based features enabled) > Notes > Lists > Features page
   */
  getDefaultHomeRoute(): string {
    const route = this.route.snapshot.queryParamMap.get('route');
    if (route) {
      return route;
    }
    const viewSettings = this.ss.viewSettings();
    const isViewSettingsLoaded = Object.keys(viewSettings).length > 0;



    // If viewSettings not loaded yet, default to /today
    if (!isViewSettingsLoaded) {
      return '/today';
    }

    const allDisabled = this.featureStore.allFeaturesDisabled();
    const dateBasedEntities = this.featureStore.dateBasedEntities();

    // If all features are disabled, go to features page
    if (allDisabled) {
      return '/features';
    }

    // If any date-based feature is enabled, go to Today
    if (dateBasedEntities.length > 0) {
      return '/today';
    }

    // If only non-date features (List) are enabled, go to Lists
    // (Notes no longer have a standalone screen — they appear in Today/Past)
    if (this.viewSettings.showListFeature()) {
      return '/lists';
    }

    // Fallback to features page
    return '/features';
  }

  navigateToHome() {
    this.clickNav();
    this.router.navigate([this.getDefaultHomeRoute()]);
  }

  openDebugScreen() {
    // Use release config feature flag (consistent with Flutter)
    if (this.ss.isFeatureFlagEnabled('debugScreen')) {
      this.dialog.open(DebugScreenComponent, {
        width: '100%',
        maxWidth: '604px'
      });
    }
  }

  /**
   * Show persistent notification to user when they are force-logged out due to
   * another user logging in from a different tab.
   * Uses NO duration so it stays visible until user dismisses it.
   */
  private showForceLogoutNotification(reason: string): void {
    log.info('PanelComponent', 'Showing force logout notification', { reason });

    this.alertService.alert(
      this.ss.t('sessionExpired_title'), // No header
      this.ss.t('sessionExpired_content'), // Message
      this.ss.t('common_ok'), // Yes button text
    )
  }

  ngOnDestroy() {
    this.unSubscribe?.next();
    this.unSubscribe?.complete();
    this.habitTimerService.destroy();
    this.ss.destroy();
    this.notificationService.destroy();
    this.stripeService.destroy();
    this.syncService.destroy();
  }
}
